import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ArrayHelpers, Copy, Forms, Help, Modal, SaveButton, SystemText } from '@salesperformanceint/e4shared';

import { setPickerFilters, toggleFormHelp } from 'actions/ui';
import { addDays, endOfDay, isPast, isWithinInterval, IsBeforeOrSameDateIgnoreTimeAndTimeZone } from 'helpers/dates';
import { apiGet } from 'services';
import BlobImage from 'components/blobImage';
import { coachRoles, isCoach } from 'helpers/profile';
import { translateCompetency } from 'helpers/translations';
import { ModalPicker } from 'components/user/modalPicker';

export const Schedule = ({ show, close, onSchedule, users, unreviewed }) => {
    const dispatch = useDispatch();

    const [hasUsersSelected, setHasUsersSelected] = React.useState(false);
    const [tab, setTab] = React.useState('assign');
    const handles = useSelector(state => state.ui.handles);
    const tags = useSelector(state => state.ui.tags);
    const { coaching, user } = useSelector(state => state.user);

    const { prism_UI } = user.companySettings;
    const prismSuffix = prism_UI ? '/Prism' : '';

    const isUsersCoach = isCoach(users?.find(x => true)?.id, user.companySettings, coaching, coachRoles.manageReviews);

    const [disabledUsers, setDisabledUsers] = React.useState([]);

    const getDefaultParticipants = () => {
        const hasZero = user.zero_Licences > 0;
        const someHaveManagers = selected.some(u => u.manager_Licence === 'FULL');
        if (!someHaveManagers) {
            if (user.reviewTypes?.some(x => x === 'U')) {
                return 'U';
            } else {
                return '';
            }
        }

        if (!hasZero && user.reviewTypes?.some(x => x === 'UM')) return 'UM';
        if (user.reviewTypes?.some(x => x === 'U')) return 'U';
        if (user.reviewTypes?.some(x => x === 'M')) return 'M';
        return 'UM';
    };

    const now = useMemo(() => new Date(), []);

    const defaultValues = {
        ids: [],
        complete: true,
        reminder: true,
        date: addDays(now, 14),
        launch: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 0),
    };

    const [competencies, setCompetencies] = useState([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [selected, setSelected] = useState([]);
    const [availableTabs, setAvailableTabs] = useState(['assign', 'set']);
    const [values, setValues] = useState(defaultValues);
    const [showParticipantWarning, setShowParticipantWarning] = useState(false);
    const [allParticipantsInvalid, setAllParticipantsInvalid] = useState(false);
    const [alwaysIncludeIds, setAlwaysIncludeIds] = useState([]);

    React.useEffect(() => {
        if (users?.length === 1 && users[0].id) {
            setAlwaysIncludeIds(users.map(u => u.id));
            setHasUsersSelected(true);
            setAvailableTabs(['set']); // prevent the user selecting the previous Tab
            setTab('set');
        }
    }, [show, users]);

    React.useEffect(() => {
        const inProgressMessage = SystemText('ReviewTracker/Schedule/DisabledUser/InProgress');
        const noManagerMessage = SystemText('ReviewTracker/Schedule/DisabledUser/Manager');
        const noCompetenciesMessage = SystemText('ReviewTracker/Schedule/DisabledUser/Competencies');
        const temp = [...users].reduce(
            (a, c) =>
                c.scheduled && !c.submission
                    ? a.concat([
                          {
                              id: c.id,
                              message: inProgressMessage,
                          },
                      ])
                    : !c.manager_Id
                      ? a.concat([
                            {
                                id: c.id,
                                message: noManagerMessage,
                            },
                        ])
                      : c.competency_Ids?.length === 0
                        ? a.concat([
                              {
                                  id: c.id,
                                  message: noCompetenciesMessage,
                              },
                          ])
                        : a,
            []
        );

        setDisabledUsers(temp);
    }, [users]);

    React.useEffect(() => {
        const then = addDays(now, -30);
        const unreviewedUsers = unreviewed
            ? users.filter(u => !u.scheduled && isWithinInterval(u.start_Date, then, now)).map(u => u.id)
            : [];
        const temp = [...users]
            .reduce((a, c) => (c.scheduled && !c.submission ? a.concat([c.id]) : a), [])
            .concat(unreviewedUsers)
            .concat(alwaysIncludeIds);

        setValue('ids', temp);
    }, [users, unreviewed]);

    useEffect(() => {
        setShowParticipantWarning(selected.some(u => !u.manager_Id));
        setAllParticipantsInvalid(!selected.some(u => u.manager_Id));
        if (!values.participants) {
            setValue('participants', getDefaultParticipants(user));
        }
    }, [selected]);

    useEffect(() => {
        if (values?.ids) {
            const temp = users.filter(u => values.ids.some(x => x === u.id));
            setSelected(temp);
        }
    }, [values?.ids]);

    React.useEffect(() => {
        const extra = selected.some(x => !x.disabled);
        setHasUsersSelected(extra);
    }, [selected]);

    useEffect(() => {
        values?.ids &&
            apiGet(`competencies-by-user?ids=${(values?.ids || []).join(',')}`, user.guid, response =>
                setCompetencies(
                    response
                        .map(c => translateCompetency(user.language_Code, c))
                        .map(r => ({ id: r.id, name: `${r.area} - ${r.name}` }))
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                )
            );
    }, [values?.ids]);

    const validate = review => {
        let result = true;
        const theseErrors = {};

        if (!review?.date || review?.date?.length === 0 || review?.date === '1970-01-01') {
            theseErrors.date = SystemText('ReviewTracker/Schedule/Fields/Date/Error');
            result = false;
        } else if (isPast(endOfDay(review.date))) {
            theseErrors.date = SystemText('ReviewTracker/Schedule/Fields/Date/Error/Past');
            result = false;
        } else if (!IsBeforeOrSameDateIgnoreTimeAndTimeZone(review.launch, review.date)) {
            theseErrors.launch = SystemText('ReviewTracker/Schedule/Fields/Launch/Error/Past');
            result = false;
        }

        if (!review.complete && !ArrayHelpers.existsAndHasLength(review.competency_Ids)) {
            theseErrors.competencies = SystemText('ReviewTracker/Schedule/Fields/Competencies/Error');
            result = false;
        }

        if (!review.participants) {
            theseErrors.participants = SystemText('ReviewTracker/Schedule/Fields/Participants/Error');
            result = false;
        }

        setErrors(theseErrors);

        if (!result) document.getElementsByClassName('modal')[0].scrollTop = 0;

        return result;
    };

    const save = () => {
        if (validate(values)) {
            onSchedule(values, () => closeModal());
        }

        setSaving(false);
    };

    const closeModal = () => {
        setErrors({});
        close?.();
        setSaving(false);
        setTab('assign');
        dispatch(setPickerFilters([]));
    };

    const setValue = (property, value) => {
        const temp = { ...values };
        temp[property] = value;
        setValues(temp);
    };

    const changeTab = () => {
        setTab(tab === 'assign' ? 'set' : 'assign');
    };

    const footer = React.useMemo(
        () => (
            <div>
                {tab === 'assign' ? (
                    <button
                        data-testid="assign-button"
                        className="btn btn-primary"
                        onClick={changeTab}
                        disabled={!hasUsersSelected}
                    >
                        {SystemText('General/Buttons/Next')}
                    </button>
                ) : (
                    <SaveButton
                        data-testid="save-button"
                        saving={saving}
                        setSaving={setSaving}
                        className="btn btn-success"
                        disabled={!hasUsersSelected}
                        onClick={save}
                    >
                        {SystemText('General/Buttons/Save')}
                    </SaveButton>
                )}
                <button data-testid="cancel-button" className="btn" onClick={() => closeModal(true)}>
                    {SystemText('General/Buttons/Cancel')}
                </button>
            </div>
        ),
        [tab, hasUsersSelected, values]
    );

    const scheduleForm = (
        <div className="form-group mt10" id="manage-task-form">
            <Forms.Date
                data-testid="field-launch-date"
                showTime
                mandatory
                value={values.launch}
                help={SystemText('ReviewTracker/Schedule/Fields/Launch/Help')}
                onChange={value => setValue('launch', value)}
                label={SystemText('ReviewTracker/Schedule/Fields/Launch/Label')}
                error={errors.launch}
            />
            <Forms.Date2
                data-testid="field-review-date"
                value={values.date}
                help={SystemText('ReviewTracker/Schedule/Fields/Date/Help')}
                onChange={value => setValue('date', value)}
                label={SystemText('ReviewTracker/Schedule/Fields/Date/Label')}
                mandatory
                error={errors.date}
            />
            <Forms.Toggle
                data-testid="field-full-toggle"
                value={values.complete}
                help={SystemText('ReviewTracker/Schedule/Fields/Full/Help')}
                onChange={value => setValue('complete', value)}
                label={SystemText('ReviewTracker/Schedule/Fields/Full/Label')}
            />
            {!values.complete && (
                <Forms.Select
                    data-testid="field-comps-select"
                    value={values.competency_Ids}
                    data={competencies || []}
                    error={errors.competencies}
                    allowMultiple
                    help={SystemText('ReviewTracker/Schedule/Fields/Competencies/Help')}
                    onChange={value => setValue('competency_Ids', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Competencies/Label')}
                />
            )}
            {user.reviewTypes?.length > 1 && (
                <>
                    <Forms.Buttons
                        data-testid="field-participants-buttons"
                        help={SystemText('ReviewTracker/Schedule/Fields/Participants/Help')}
                        label={SystemText('ReviewTracker/Schedule/Fields/Participants/Label')}
                    >
                        {user.reviewTypes?.some(x => x === 'U') && (
                            <button
                                data-test-id="button-participants-user"
                                className={`btn ${values.participants === 'U' ? 'btn-primary' : ''}`}
                                onClick={() => setValue('participants', 'U')}
                            >
                                {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/User')}
                            </button>
                        )}
                        {user.reviewTypes?.some(x => x === 'M') && (
                            <button
                                data-test-id="button-participants-manager"
                                disabled={allParticipantsInvalid}
                                title={
                                    allParticipantsInvalid
                                        ? SystemText('ReviewTracker/Schedule/Fields/Participants/M/Disabled')
                                        : ''
                                }
                                className={`btn ${values.participants === 'M' ? 'btn-primary' : ''}`}
                                onClick={() => setValue('participants', 'M')}
                            >
                                {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/Manager')}
                            </button>
                        )}
                        {user.reviewTypes?.some(x => x === 'UM') && (
                            <button
                                data-test-id="button-participants-usermanager"
                                disabled={allParticipantsInvalid}
                                title={
                                    allParticipantsInvalid
                                        ? SystemText('ReviewTracker/Schedule/Fields/Participants/UM/Disabled')
                                        : ''
                                }
                                className={`btn ${values.participants === 'UM' ? 'btn-primary' : ''}`}
                                onClick={() => setValue('participants', 'UM')}
                            >
                                {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/Both')}
                            </button>
                        )}
                    </Forms.Buttons>
                    {isUsersCoach && (
                        <div className="mtm15 mb20 text-sm">
                            {SystemText('ReviewTracker/Schedule/Fields/Participants/CoachExplainer')}
                        </div>
                    )}
                    {showParticipantWarning && ['M', 'UM'].some(x => x === values.participants) && (
                        <div className="mtm5 mb10 flex flex-center">
                            <i className="fa fa-warning text-danger text-lg mr10" />
                            <span className="flex-grow text-sm">
                                {SystemText('ReviewTracker/Schedule/Fields/Participants/Warning')}
                                <Help id="participant-help">
                                    <div
                                        className="text-center"
                                        style={{
                                            paddingBottom: '10px',
                                            marginBottom: '10px',
                                            borderBottom: 'solid 1px #A0A0A0',
                                        }}
                                    >
                                        {SystemText('ReviewTracker/Schedule/Fields/Participants/Warning/Title')}
                                    </div>
                                    {selected
                                        .filter(u => u.manager_Licence !== 'FULL')
                                        .map(u => (
                                            <div className="flex flex-center mb5" key={`non-manager-${u.id}`}>
                                                <BlobImage
                                                    height="30"
                                                    className="img-square mr10"
                                                    src={u.avatar}
                                                    folder="avatar"
                                                    initials={u.initials}
                                                    alt={u.full_Name}
                                                />
                                                <span className="flex-grow">{u.full_Name}</span>
                                            </div>
                                        ))}
                                </Help>
                            </span>
                        </div>
                    )}
                </>
            )}
            {['U', 'UM'].some(x => x === values.participants) && (
                <Forms.Textarea
                    data-testid="field-review-invite"
                    id="field-review-invite"
                    value={values.invite}
                    infoHelp={
                        <div>
                            <h4>{SystemText('ReviewTracker/Schedule/Fields/DefaultIntro')}</h4>
                            <Copy value={SystemText(`Notification/Zero_Assign/Message${prismSuffix}`, 'John Smith')} />
                        </div>
                    }
                    rows="6"
                    onChange={value => setValue('invite', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Invite/Label')}
                    error={errors.invite}
                    help={SystemText('ReviewTracker/Schedule/Fields/Invite/Help')}
                    placeholder={SystemText('ReviewTracker/Schedule/Fields/Invite/Placeholder')}
                    tags={tags}
                    handles={handles}
                />
            )}
            {['M', 'UM'].some(x => x === values.participants) && (
                <Forms.Textarea
                    data-testid="field-review-manager-invite"
                    id="field-review-manager-invite"
                    value={values.managerInvite}
                    infoHelp={
                        <div>
                            <h4>{SystemText('ReviewTracker/Schedule/Fields/DefaultIntro')}</h4>
                            <Copy
                                value={SystemText(`Notification/Zero_Assign_Manager/Message${prismSuffix}`, 'John Smith', 'Kate Brown')}
                            />
                        </div>
                    }
                    rows="6"
                    onChange={value => setValue('managerInvite', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/ManagerInvite/Label')}
                    error={errors.managerInvite}
                    help={SystemText('ReviewTracker/Schedule/Fields/ManagerInvite/Help')}
                    placeholder={SystemText('ReviewTracker/Schedule/Fields/ManagerInvite/Placeholder')}
                    tags={tags}
                    handles={handles}
                />
            )}
            <Forms.Toggle
                data-testid="field-review-reminder"
                value={values.reminder}
                onChange={value => setValue('reminder', value)}
                label={SystemText('ReviewTracker/Schedule/Fields/Reminder/Label')}
                help={SystemText('ReviewTracker/Schedule/Fields/Reminder/Help')}
            />
            {values.participants === 'UM' && (
                <Forms.Toggle
                    data-testid="field-review-manager"
                    value={values.ccManager}
                    onChange={value => setValue('ccManager', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Manager/Label')}
                    help={SystemText('ReviewTracker/Schedule/Fields/Manager/Help')}
                />
            )}
        </div>
    );

    return (
        <Modal
            data-testid="post-modal"
            show={show}
            close={closeModal}
            footer={footer}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
            tab={tab}
            setTab={setTab}
            className="modal-body-full modal-920"
            tabs={availableTabs}
            tabIcons={
                tab === 'assign'
                    ? ['fa-circle-1 fa-solid fa-tab', 'fa-circle-2 fa-tab']
                    : ['fa-circle-check fa-tab', 'fa-circle-2 fa-solid fa-tab']
            }
            iconsPre
            tabNames={{
                assign: SystemText('ReviewTracker/Schedule/Tabs/Assign'),
                set: SystemText('ReviewTracker/Schedule/Tabs/Set'),
            }}
        >
            {tab === 'assign' ? (
                <ModalPicker value={values?.ids} disabledUsers={disabledUsers} onChange={ids => setValue('ids', ids)} />
            ) : (
                <div>
                    {hasUsersSelected ? (
                        scheduleForm
                    ) : (
                        <div className="mb20 mt20 flex flex-center">
                            <i className="fa fa-warning fa-lg mr10 text-danger" />
                            <span className="flex-grow">
                                <Copy value={SystemText('ReviewTracker/Schedule/NoUsers')} />
                            </span>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default Schedule;
